/**
 * Settings → Integrations — prototype ST.tab==='integr' (docs/prototype.html L4434–4436). GET /integrations:
 *  - Zoho billing: its state with the last success and, loudly, the last error or "not configured"; "Sync now"
 *    (POST /integrations/zoho/sync, followed through GET /jobs/runs?job=zoho.sync); the invoice lines still needing
 *    a product (GET /integrations/zoho/invoice-lines?mapped=false), each with a product select (POST …/{id}/map).
 *  - Email tracking, Calendar and AI summaries: state and last success (speech to text is browser-only, so not listed).
 * Until Zoho first syncs, the Money view runs on the per-product MRR in Settings (decision 16) and says so.
 */
import type { IntegrationState, IntegrationStatus } from '@amplispot/contracts';
import { useIntegrations, useMapLine, useUnmappedLines, useZohoRuns, useZohoSync, inr, syncedAt } from '../../lib/money';
import { useManyClientProducts } from '../../lib/usage';
import { useSession } from '../../lib/session';
import { ErrorState, Loading, errorText, useToast } from '../../ui/kit';

const STATUS: Record<IntegrationStatus, [string, string]> = {
  connected: ['hot', 'Connected'],
  stale: ['warm', 'Behind'],
  error: ['cold', 'Failing'],
  off: ['', 'Off'],
};

export function Integrations() {
  const q = useIntegrations();
  if (q.isPending) return <div className="panel"><Loading /></div>;
  if (q.isError) return <div className="panel"><ErrorState error={q.error} onRetry={() => q.refetch()} /></div>;
  const zoho = q.data.data.find((i) => i.integration === 'zoho');
  const rest = q.data.data.filter((i) => i.integration !== 'zoho');
  return (
    <div className="cols c2">
      {zoho ? <Zoho z={zoho} /> : <div className="panel"><div className="bd">Zoho billing is not listed by the server.</div></div>}
      <div style={{ display: 'grid', gap: 16, alignContent: 'start' }}>
        {rest.map((i) => (
          <div key={i.integration} className="panel">
            <div className="hd">
              <h3>{i.label}</h3>
              <span className={`chip ${STATUS[i.status][0]}`}>{STATUS[i.status][1]}</span>
            </div>
            <div className="bd sub" style={{ color: 'var(--sub)' }}>
              {i.last_error ? <div style={{ color: 'var(--cold)', marginBottom: 4 }}>{i.last_error}</div> : null}
              {i.last_success_at ? `Last worked ${syncedAt(i.last_success_at)}.` : i.status === 'off' ? 'Not set up yet.' : 'Has not run yet.'}
              {i.cursor_summary ? ` ${i.cursor_summary}.` : ''}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Zoho({ z }: { z: IntegrationState }) {
  const { can } = useSession();
  const write = can('settings.write');
  const toast = useToast();
  const sync = useZohoSync();
  const runs = useZohoRuns();
  const lines = useUnmappedLines();
  // POST …/invoice-lines/{id}/map answers 422 for a product the client does not buy: offer only the matched client's.
  const clientIds = [...new Set((lines.data ?? []).flatMap((l) => (l.client_match.client ? [l.client_match.client.id] : [])))];
  const products = useManyClientProducts(clientIds);
  const map = useMapLine();
  const running = runs.data?.data.find((r) => r.status === 'running');
  const last = runs.data?.data[0];
  return (
    <div className="panel" style={{ alignSelf: 'start' }}>
      <div className="hd">
        <h3>{z.label}</h3>
        <div className="r">
          <span className={`chip ${STATUS[z.status][0]}`}>{STATUS[z.status][1]}</span>
          {write ? (
            <button
              className="btn sm"
              disabled={sync.isPending || !!running}
              onClick={() => sync.mutate(undefined, { onSuccess: () => toast('Sync started'), onError: (e) => toast(errorText(e)) })}
            >
              {running ? 'Syncing…' : 'Sync now'}
            </button>
          ) : null}
        </div>
      </div>
      <div className="bd">
        {z.status === 'off' || z.status === 'error' ? (
          <div className="note bad" style={{ marginBottom: 10 }}>
            <b>{z.status === 'off' ? 'Zoho is not connected.' : 'The last Zoho sync failed.'}</b>{' '}
            {z.last_error ?? (z.status === 'off' ? 'Add the Zoho Books and Subscriptions credentials (India data centre) to start the nightly pull.' : 'Zoho did not say why.')}{' '}
            {/* Decision 16: once Zoho has synced, it is the only source, and a failed sync keeps the last Zoho figures. */}
            {z.last_success_at
              ? `Figures are from the last successful sync ${syncedAt(z.last_success_at)}.`
              : 'Until it syncs, the Money view uses the monthly revenue per product typed in Settings, and says so.'}
          </div>
        ) : null}
        <div className="sub" style={{ color: 'var(--sub)', marginBottom: 10 }}>
          Nightly one-way pull of clients, plans and invoices from Zoho Books and Subscriptions.{' '}
          {z.last_success_at ? `Last sync ${syncedAt(z.last_success_at)}.` : 'It has never synced.'}
          {z.cursor_summary ? ` ${z.cursor_summary}.` : ''}
        </div>
        {last ? (
          <div className="sub" style={{ marginBottom: 12, fontSize: 12.5, color: last.status === 'failed' ? 'var(--cold)' : 'var(--sub)' }}>
            Latest run {syncedAt(last.started_at)}: {last.status === 'running' ? 'running' : last.status === 'ok' ? 'done' : `failed${last.error ? `, ${last.error}` : ''}`}
            {last.triggered_by ? ` (started by ${last.triggered_by.name})` : ''}.
          </div>
        ) : null}
        <div className="lbl" style={{ marginBottom: 6 }}>
          Invoice lines needing a product{z.unmapped_lines ? ` (${z.unmapped_lines})` : ''}
        </div>
        {lines.isPending ? (
          <Loading />
        ) : lines.isError ? (
          <ErrorState error={lines.error} onRetry={() => lines.refetch()} />
        ) : lines.data.length ? (
          lines.data.map((l) => (
            <div key={l.id} className="split" style={{ marginBottom: 6 }}>
              <span className="grow" style={{ fontSize: 13 }}>
                {l.description}, {l.client_match.client?.name ?? l.client_match.zoho_customer_name}
                <span className="sub"> · {inr(l.amount_lakh)} · {l.invoice_number}</span>
              </span>
              <select
                className="in"
                style={{ width: 170 }}
                value=""
                disabled={!write || !l.client_match.client || map.isPending}
                onChange={(e) =>
                  e.target.value &&
                  map.mutate({ id: l.id, product_key: e.target.value }, { onSuccess: () => toast('Saved'), onError: (er) => toast(errorText(er)) })
                }
                aria-label="Product"
              >
                <option value="">
                  {!l.client_match.client
                    ? 'No client matched'
                    : products.productsOf(l.client_match.client.id)
                      ? products.productsOf(l.client_match.client.id)!.length
                        ? 'Choose product'
                        : 'The client buys no product yet'
                      : products.error
                        ? 'Products did not load'
                        : 'Loading products…'}
                </option>
                {(l.client_match.client ? products.productsOf(l.client_match.client.id) ?? [] : []).map((p) => (
                  <option key={p.product_key} value={p.product_key}>
                    {p.product_name}
                  </option>
                ))}
              </select>
            </div>
          ))
        ) : (
          <div className="sub" style={{ color: 'var(--sub)' }}>
            {z.last_success_at ? 'Every invoice line has a product.' : 'Lines appear here after the first sync.'}
          </div>
        )}
      </div>
    </div>
  );
}
